import React, { useEffect } from "react";
import { Product } from "../../types/Product";
import { NextPage, NextPageContext } from "next";
import { notAuthorised } from "../../request";
import Header from "../../components/Header";
import { getCommonData } from "../../helpers/common-data";
import { CommonData } from "../../types/CommonData";
import Footer from "../../components/Footer";
import { Row, Spacing } from "../../helpers/layout";
import { css } from "styled-components";
import { Wrapper } from "../../components/Wrapper";
import { PrintProductDetail } from "../../components/PrintProductDetail";
import { DefaultProductDetail } from "../../components/DefaultProductDetail";
import { RecentlyViewed } from "../../components/RecentlyViewed";
import { Link } from "../../components/Link";
import { Announcement } from "../../types/Announcement";
import { getAnnouncements } from "../../helpers/getAnnouncements";
import { PlainButton } from "../../components/PlainButton";
import ArrowLeft from "../../icons/ArrowLeft";
import ArrowRight from "../../icons/ArrowRight";
import Router from "next/router";
import { useResponsive } from "../../context/ResponsiveContext";
// import { useModal } from "../../context/ModalContext";
// import { useUserState } from "../../context/UserContext";
import { useChatbox } from "../../helpers/useChatbox";
import { getRichSnippet } from "../../helpers/rich-snippets/product";
import { getFrontendUrl } from "../../helpers/utils";
import { mixpanelEvent } from "../../helpers/analytics";
import { SEOTags } from "../../components/SEOTags";
import Error from "../_error";
import gtm from "helpers/gtm";
import { useCommonData } from "context/CommonDataContext";

export const ProductDetail: NextPage<{
  data: Product | null;
  commonData: CommonData;
  announcements: Announcement[];
  isPinterestBot: boolean;
}> = (props) => {
  const { isMobile } = useResponsive();
  // const { showModal } = useModal();
  // const { isAuthenticated } = useUserState();
  const product = props.data;
  const isPhotoProduct = product
    ? product.type === "PHOTO" || product.type === "PAIRING"
    : false;
  const { settings } = useCommonData();

  useChatbox();

  useEffect(() => {
    mixpanelEvent("Product view");

    if (product) {
      gtm.viewProduct(product, settings);
      gtm.pageView([product.id.toString()]);
    }
  }, [product, settings]);

  // // HIDE PRICES FROM GUESTS
  // useEffect(() => {
  //   if (isAuthenticated === false && !isMobile && isPhotoProduct) {
  //     showModal("login", {
  //       type: "product",
  //     });
  //   }
  // }, [isAuthenticated, isMobile, isPhotoProduct, showModal]);

  if (!product) {
    return (
      <Error
        commonData={props.commonData}
        announcements={props.announcements}
        statusCode={404}
      />
    );
  }

  return (
    <>
      <SEOTags
        title={product.name}
        description={product.description}
        type="product"
        keywords={product.keywords}
        url={getFrontendUrl(`/shop/${product.slug}`)}
        featuredImage={product.featured_image}
        richSnippets={getRichSnippet(product, props.isPinterestBot)}
        noIndex={product.visibility === "PRIVATE"}
      />
      <Header announcements={props.announcements} />

      <Wrapper type="header">
        <div
          css={css`
            padding: ${Spacing.l} 0;
          `}
        >
          {product.serie && (
            <Row justify="space-between">
              <nav>
                <Row>
                  <Link href={`/series/${product.serie.slug}/shop`}>
                    Return to Series
                  </Link>
                  <div>/</div>
                  <Link href={`/series/${product.serie.slug}`}>
                    About the Series
                  </Link>
                </Row>
              </nav>
              <div
                css={css`
                  svg {
                    height: 13px;
                  }
                `}
              >
                <Row justify="space-between" gutter={Spacing.xl}>
                  <PlainButton
                    disabled={!product.previous}
                    onClick={() => {
                      Router.push(
                        "/shop/[slug]",
                        `/shop/${product.previous!.slug}`,
                      );
                    }}
                    iconPosition="left"
                    icon={<ArrowLeft />}
                  >
                    {isMobile ? "" : "Previous"}
                  </PlainButton>
                  <PlainButton
                    disabled={!product.next}
                    onClick={() => {
                      Router.push(
                        "/shop/[slug]",
                        `/shop/${product.next!.slug}`,
                      );
                    }}
                    iconPosition="right"
                    icon={<ArrowRight />}
                  >
                    {isMobile ? "" : "Next"}
                  </PlainButton>
                </Row>
              </div>
            </Row>
          )}
        </div>
      </Wrapper>
      <article>
        {isPhotoProduct ? (
          <PrintProductDetail product={product} />
        ) : (
          <DefaultProductDetail product={product} />
        )}
      </article>
      <RecentlyViewed productId={product.id} />
      <Footer />
    </>
  );
};

interface Context extends NextPageContext {
  query: {
    slug: string;
  };
}

ProductDetail.getInitialProps = async (ctx: Context) => {
  let data = null;
  const userAgent = ctx.req?.headers["user-agent"] || "";
  const isPinterestBot = !!userAgent?.toLowerCase().includes("pinterestbot");

  try {
    const response = await notAuthorised.request<Product>({
      method: "GET",
      url: `/api/frontend/products/${ctx.query.slug}/`,
    });

    data = response.data;
  } catch (error) {
    if (ctx.res) {
      ctx.res.statusCode = (error as any).response.status;
    }
  }

  if (ctx.res && data) {
    if (
      data.visibility === "ARCHIVED" ||
      (data.serie && data.serie.visibility === "ARCHIVED")
    ) {
      ctx.res.writeHead(301, {
        Location: "/retired-images",
      });

      ctx.res.end();
      return {} as any;
    }

    if (data.visibility === "RETIRED") {
      ctx.res.writeHead(301, {
        Location: "/retired",
      });

      ctx.res.end();
      return {} as any;
    }
    if (
      data.visibility === "DRAFT" &&
      !ctx.req?.headers?.host?.includes("preview")
    ) {
      data = null;
    }
  }

  const commonData = await getCommonData(ctx.req, "PRODUCT");
  const announcements = await getAnnouncements("SHOP");

  return {
    data: data,
    commonData: commonData,
    announcements,
    isPinterestBot,
  };
};
export default ProductDetail;
